import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { CartProvider } from './context/CartContext';
import { Toaster } from './components/ui/sonner';
import PWAInstallPrompt from './components/PWAInstallPrompt';

// Pages
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import DashboardPage from './pages/DashboardPage';
import SuppliersPage from './pages/SuppliersPage';
import CatalogPage from './pages/CatalogPage';
import CartPage from './pages/CartPage';
import OrdersPage from './pages/OrdersPage';
import OrderDetailPage from './pages/OrderDetailPage';
import DeliveriesPage from './pages/DeliveriesPage';
import ReceptionDetailPage from './pages/ReceptionDetailPage';
import DeliveryReceptionPage from './pages/DeliveryReceptionPage';
import AdminUsersPage from './pages/AdminUsersPage';
import AdminSuppliersPage from './pages/AdminSuppliersPage';
import AdminProductsPage from './pages/AdminProductsPage';
import AdminTeamsPage from './pages/AdminTeamsPage';
import RestaurantSettingsPage from './pages/RestaurantSettingsPage';
import SuperAdminPage from './pages/SuperAdminPage';
import OrderTemplatesPage from './pages/OrderTemplatesPage';
import OnboardingPage from './pages/OnboardingPage';
import ProductDocumentation from './pages/ProductDocumentation';

// Super Admin Pages
import SuperAdminLoginPage from './pages/SuperAdminLoginPage';
import SuperAdminDashboardPage from './pages/SuperAdminDashboardPage';
import SuperAdminAlertsPage from './pages/SuperAdminAlertsPage';
import SuperAdminRestaurantsPage from './pages/SuperAdminRestaurantsPage';
import SuperAdminRestaurantDetailPage from './pages/SuperAdminRestaurantDetailPage';
import SuperAdminSettingsPage from './pages/SuperAdminSettingsPage';
import SuperAdminBackupsPage from './pages/SuperAdminBackupsPage';

// Layout
import Layout from './components/Layout';
import SuperAdminLayout from './components/SuperAdminLayout';

const Spinner = () => (
  <div className="min-h-screen flex items-center justify-center bg-[#F9FAFB]">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1B4332]"></div>
  </div>
);

const ProtectedRoute = ({ children, requireAdmin = false, requireSuperAdmin = false }) => {
  const { user, loading } = useAuth();
  if (loading) return <Spinner />;
  if (!user) return <Navigate to="/login" replace />;
  if (requireSuperAdmin && !user.is_super_admin) return <Navigate to="/catalog" replace />;
  if (requireAdmin && !user.is_admin && !user.is_super_admin) return <Navigate to="/catalog" replace />;
  return children;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <Spinner />;
  if (user) {
    if (user.is_super_admin) return <Navigate to="/super-admin" replace />;
    return <Navigate to={user.is_admin ? "/dashboard" : "/catalog"} replace />;
  }
  return children;
};

const ProtectedSuperAdminRoute = ({ children }) => {
  const token = localStorage.getItem('superadmin_token');
  if (!token) return <Navigate to="/orderlab-admin/login" replace />;
  return children;
};

const SuperAdminPublicRoute = ({ children }) => {
  const token = localStorage.getItem('superadmin_token');
  if (token) return <Navigate to="/orderlab-admin/dashboard" replace />;
  return children;
};

const AppRoutes = () => {
  return (
    <Routes>
      {/* Public Landing Page */}
      <Route path="/landing" element={<LandingPage />} />
      
      {/* Public Documentation */}
      <Route path="/documentation/produits" element={<ProductDocumentation />} />
      
      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
      <Route path="/onboarding" element={<ProtectedRoute><OnboardingPage /></ProtectedRoute>} />
      
      {/* Super Admin Routes (Separate Section) */}
      <Route path="/orderlab-admin/login" element={<SuperAdminPublicRoute><SuperAdminLoginPage /></SuperAdminPublicRoute>} />
      <Route path="/orderlab-admin" element={<ProtectedSuperAdminRoute><SuperAdminLayout /></ProtectedSuperAdminRoute>}>
        <Route index element={<Navigate to="/orderlab-admin/dashboard" replace />} />
        <Route path="dashboard" element={<SuperAdminDashboardPage />} />
        <Route path="alerts" element={<SuperAdminAlertsPage />} />
        <Route path="restaurants" element={<SuperAdminRestaurantsPage />} />
        <Route path="restaurants/:id" element={<SuperAdminRestaurantDetailPage />} />
        <Route path="backups" element={<SuperAdminBackupsPage />} />
        <Route path="settings" element={<SuperAdminSettingsPage />} />
      </Route>
      
      <Route path="/" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
        <Route index element={<Navigate to="/catalog" replace />} />
        <Route path="dashboard" element={<ProtectedRoute requireAdmin><DashboardPage /></ProtectedRoute>} />
        <Route path="suppliers" element={<SuppliersPage />} />
        <Route path="catalog" element={<CatalogPage />} />
        <Route path="cart" element={<CartPage />} />
        <Route path="orders" element={<OrdersPage />} />
        <Route path="orders/:orderId" element={<OrderDetailPage />} />
        <Route path="deliveries" element={<DeliveriesPage />} />
        <Route path="deliveries/:orderId" element={<ReceptionDetailPage />} />
        <Route path="deliveries/:orderId/reception" element={<DeliveryReceptionPage />} />
        <Route path="admin/modeles" element={<OrderTemplatesPage />} />

        {/* Admin Routes */}
        <Route path="admin/users" element={<ProtectedRoute requireAdmin><AdminUsersPage /></ProtectedRoute>} />
        <Route path="admin/teams" element={<ProtectedRoute requireAdmin><AdminTeamsPage /></ProtectedRoute>} />
        <Route path="admin/suppliers" element={<ProtectedRoute requireAdmin><AdminSuppliersPage /></ProtectedRoute>} />
        <Route path="admin/products" element={<ProtectedRoute requireAdmin><AdminProductsPage /></ProtectedRoute>} />
        <Route path="admin/settings" element={<ProtectedRoute requireAdmin><RestaurantSettingsPage /></ProtectedRoute>} />
        <Route path="super-admin" element={<ProtectedRoute requireSuperAdmin><SuperAdminPage /></ProtectedRoute>} />
      </Route>

      <Route path="*" element={<Navigate to="/landing" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <CartProvider>
          <AppRoutes />
          <Toaster position="bottom-center" richColors />
          <PWAInstallPrompt />
        </CartProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
